import React, { useState, useMemo } from 'react';
import { IShow } from "@eagerdog/interfaces";
import { format } from "date-fns";

import Button from "../Button/Button";
import Checkbox from "../Checkbox/Checkbox";

import "./ArmbandFilterModal.scss";

interface IProps {
  shows: IShow[];
  onDownload: (params: { show_ids?: string[]; arm_numbers?: number[] }) => void;
  onCancel: () => void;
}

type FilterType = 'all' | 'selected' | 'numbers';

function parseArmbandNumbers(input: string): number[] {
  const numbers = new Set<number>();
  const parts = input.split(',').map(s => s.trim()).filter(Boolean);
  for (const part of parts) {
    if (part.includes('-')) {
      const [startStr, endStr] = part.split('-');
      const start = parseInt(startStr, 10);
      const end = parseInt(endStr, 10);
      if (!isNaN(start) && !isNaN(end) && start > 0 && end >= start && (end - start) <= 500) {
        for (let i = start; i <= end; i++) numbers.add(i);
      }
    } else {
      const num = parseInt(part, 10);
      if (!isNaN(num) && num > 0) numbers.add(num);
    }
  }
  return Array.from(numbers).sort((a, b) => a - b);
}

const ArmbandFilterModal: React.FC<IProps> = (props) => {
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [selectedShowIds, setSelectedShowIds] = useState<string[]>([]);
  const [numberInput, setNumberInput] = useState<string>('');

  const groupedShows = useMemo(() => {
    const groups = new Map<string, IShow[]>();
    const sortedShows = [...props.shows].sort((a, b) =>
      new Date(a.show_date).getTime() - new Date(b.show_date).getTime()
    );
    sortedShows.forEach(show => {
      const existing = groups.get(show.show_type) || [];
      existing.push(show);
      groups.set(show.show_type, existing);
    });
    return groups;
  }, [props.shows]);

  const parsedNumbers = useMemo(() => parseArmbandNumbers(numberInput), [numberInput]);

  const numberInputError = useMemo(() => {
    if (!numberInput.trim()) return null;
    if (parsedNumbers.length === 0) return 'No valid armband numbers found. Use formats like: 1, 5, 10-15';
    return null;
  }, [numberInput, parsedNumbers]);

  const handleFilterTypeChange = (type: FilterType) => {
    setFilterType(type);
    if (type === 'all') {
      setSelectedShowIds([]);
    }
  };

  const handleShowToggle = (showId: string) => {
    setSelectedShowIds(prev => {
      if (prev.includes(showId)) {
        return prev.filter(id => id !== showId);
      } else {
        return [...prev, showId];
      }
    });
  };

  const handleSelectAll = () => {
    setSelectedShowIds(props.shows.map(s => s._id));
  };

  const handleSelectNone = () => {
    setSelectedShowIds([]);
  };

  const handleDownload = () => {
    if (filterType === 'all') {
      props.onDownload({});
    } else if (filterType === 'selected') {
      props.onDownload({ show_ids: selectedShowIds });
    } else {
      props.onDownload({ arm_numbers: parsedNumbers });
    }
  };

  const isDownloadDisabled = () => {
    if (filterType === 'selected') return selectedShowIds.length === 0;
    if (filterType === 'numbers') return parsedNumbers.length === 0;
    return false;
  };

  const formatShowDate = (date: Date) => {
    try {
      return format(new Date(date), 'MMM d, yyyy');
    } catch {
      return 'Unknown date';
    }
  };

  const formatShowType = (showType: string) => {
    return showType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className="armband-filter-modal">
      <h2>Download Armband Labels</h2>
      <p>Select which armbands to include in the download:</p>

      <div className="scrollable-content">
        <div className="filter-options">
          <label className={`radio-option ${filterType === 'all' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="filterType"
              value="all"
              checked={filterType === 'all'}
              onChange={() => handleFilterTypeChange('all')}
            />
            <div className="option-content">
              <div className="option-title">All Armbands</div>
              <div className="option-description">
                Generate armband labels for all {props.shows.length} trial{props.shows.length !== 1 ? 's' : ''}
              </div>
            </div>
          </label>

          <label className={`radio-option ${filterType === 'selected' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="filterType"
              value="selected"
              checked={filterType === 'selected'}
              onChange={() => handleFilterTypeChange('selected')}
            />
            <div className="option-content">
              <div className="option-title">Selected Trials</div>
              <div className="option-description">
                Choose specific trials to include
              </div>
            </div>
          </label>

          <label className={`radio-option ${filterType === 'numbers' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="filterType"
              value="numbers"
              checked={filterType === 'numbers'}
              onChange={() => handleFilterTypeChange('numbers')}
            />
            <div className="option-content">
              <div className="option-title">Specific Armband Numbers</div>
              <div className="option-description">
                Print only selected armband numbers or ranges
              </div>
            </div>
          </label>
        </div>

        {filterType === 'selected' && (
          <div className="selection-section">
            <div className="section-header">
              <div className="section-title">Select Trials</div>
              <div className="quick-actions">
                <button type="button" onClick={handleSelectAll}>Select All</button>
                <span className="divider">|</span>
                <button type="button" onClick={handleSelectNone}>Select None</button>
              </div>
            </div>

            <div className="shows-list">
              {Array.from(groupedShows.entries()).map(([showType, shows]) => (
                <div key={showType} className="show-type-group">
                  <div className="show-type-header">{formatShowType(showType)}</div>
                  {shows.map(show => (
                    <div key={show._id} className="show-item">
                      <Checkbox
                        id={`show-${show._id}`}
                        value={selectedShowIds.includes(show._id)}
                        onChange={() => handleShowToggle(show._id)}
                        label={`${show.custom_show_name || show.show_name} - ${formatShowDate(show.show_date)}`}
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <div className="selection-summary">
              {selectedShowIds.length} trial{selectedShowIds.length !== 1 ? 's' : ''} selected
            </div>
          </div>
        )}

        {filterType === 'numbers' && (
          <div className="selection-section numbers-section">
            <div className="section-title">Enter Armband Numbers</div>
            <div className="numbers-hint">
              Enter individual numbers or ranges separated by commas.
              <br />
              <span className="numbers-example">Example: <code>1, 5, 10-15, 22</code></span>
            </div>
            <input
              className={`numbers-input${numberInputError ? ' has-error' : ''}`}
              type="text"
              placeholder="e.g. 1, 5, 10-15, 22"
              value={numberInput}
              onChange={e => setNumberInput(e.target.value)}
              autoFocus
            />
            {numberInputError && (
              <div className="numbers-error">{numberInputError}</div>
            )}
            {parsedNumbers.length > 0 && !numberInputError && (
              <div className="numbers-preview">
                {parsedNumbers.length} armband{parsedNumbers.length !== 1 ? 's' : ''} selected:&nbsp;
                <span className="preview-list">
                  {parsedNumbers.length <= 20
                    ? parsedNumbers.join(', ')
                    : `${parsedNumbers.slice(0, 20).join(', ')} … and ${parsedNumbers.length - 20} more`}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="modal-actions">
        <Button
          className="secondary"
          onClick={props.onCancel}
        >
          Cancel
        </Button>
        <Button
          onClick={handleDownload}
          disabled={isDownloadDisabled()}
        >
          Download
        </Button>
      </div>
    </div>
  );
};

export default ArmbandFilterModal;
